/**
 * Clock Out Dialog Component
 * Displays session information, captures signature, location, and signee details
 */

import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Checkbox,
  FormControlLabel,
  TextField,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  InputLabel,
  Divider,
  Grid,
  Slide,
} from '@mui/material'
import {
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  LocalHospital as TherapyIcon,
} from '@mui/icons-material'
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from '@react-google-maps/api'
import SignatureCanvas from 'react-signature-canvas'
import { format } from 'date-fns'
import { Session, Patient, Therapist, SessionStatus } from '@/types/contracts'
import api from '@/services/api'
import EarthLoader from '@/components/loaders/EarthLoader'
import { relationshipOptions } from '@/constants/familyConstants'
import { GOOGLE_MAPS_LIBRARIES, getGoogleMapsApiKey } from '@/constants/googleMaps'

interface ClockOutDialogProps {
  open: boolean
  onClose: () => void
  session: Session
  patient: Patient
  therapist: Therapist
  contractId: string
  onSuccess: (updatedSession: Session) => void
}

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px',
}

const defaultCenter = {
  lat: 34.0522,
  lng: -118.2437,
}

const ClockOutDialog: React.FC<ClockOutDialogProps> = ({
  open,
  onClose,
  session,
  patient,
  therapist,
  contractId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [location, setLocation] = useState<{
    lat: number
    lng: number
    address: string
  } | null>(null)
  const [locationError, setLocationError] = useState<string>('')
  const [verifying, setVerifying] = useState(false)
  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [infoWindowOpen, setInfoWindowOpen] = useState(true)
  const [isAgree, setIsAgree] = useState(false)
  const [signedByType, setSignedByType] = useState<'patient' | 'family'>(
    'patient'
  )
  const [relationship, setRelationship] = useState('')
  const [signedByName, setSignedByName] = useState('')
  const [hasSignature, setHasSignature] = useState(false)
  const signaturePadRef = useRef<SignatureCanvas>(null)

  const apiKey = getGoogleMapsApiKey()
  // Use constant libraries array to prevent recreation on each render
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  // Fetch location when dialog opens
  useEffect(() => {
    if (open) {
      fetchLocation()
      // Reset form when dialog opens
      setIsAgree(false)
      setSignedByType('patient')
      setRelationship('')
      setSignedByName('')
      setHasSignature(false)
      if (signaturePadRef.current) {
        signaturePadRef.current.clear()
      }
    } else {
      // Reset state when dialog closes
      setLocation(null)
      setLocationError('')
      setVerifying(false)
    }
  }, [open])

  const fetchLocation = async () => {
    setVerifying(true)
    setLocationError('')
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser')
      }

      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            maximumAge: 0,
          })
        }
      )

      const lat = position.coords.latitude
      const lng = position.coords.longitude

      // Set map center immediately
      setMapCenter({ lat, lng })

      // Try to get address from reverse geocoding
      let address = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`

      // Set initial location with coordinates
      setLocation({ lat, lng, address })

      // Try to get formatted address if Google Maps is loaded
      if (window.google?.maps && isLoaded) {
        try {
          const geocoder = new window.google.maps.Geocoder()
          const latlng = { lat, lng }
          geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              setLocation({
                lat,
                lng,
                address: results[0].formatted_address,
              })
            }
          })
        } catch (error) {
          // If geocoding fails, keep the coordinate-based address
          console.warn('Geocoding failed:', error)
        }
      }
    } catch (error: any) {
      setLocationError(
        error.message ||
          'Unable to retrieve location. Please check your location permissions.'
      )
    } finally {
      setVerifying(false)
    }
  }

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear()
      setHasSignature(false)
    }
  }

  const handleSignatureEnd = () => {
    if (signaturePadRef.current) {
      setHasSignature(!signaturePadRef.current.isEmpty())
    }
  }

  const handleClockOut = async () => {
    if (!location) {
      setLocationError('Location is required to clock out')
      return
    }

    if (!isAgree) {
      setLocationError('Please acknowledge that you received therapy services')
      return
    }

    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      setLocationError('Please provide a signature')
      return
    }

    // Only validate name and relationship when Guardian is selected
    if (signedByType === 'family') {
      if (!relationship) {
        setLocationError('Please select relationship with patient')
        return
      }
      if (!signedByName.trim()) {
        setLocationError('Please enter the signee name')
        return
      }
    }

    setLoading(true)
    try {
      const signatureData = signaturePadRef.current.toDataURL()

      // Prepare payload based on signed_by_type
      const payload: any = {
        contract_id: contractId,
        session_id: session._id || session.id,
        lat: location.lat.toString(),
        long: location.lng.toString(),
        is_agree: isAgree,
        signature: signatureData,
        signed_by_type: signedByType === 'patient' ? 'patient' : 'family',
      }

      // Add relation and signed_by_name only for family, set to null for patient
      if (signedByType === 'patient') {
        payload.relation = null
        payload.signed_by_name = null
      } else {
        payload.relation = relationship
        payload.signed_by_name = signedByName
      }

      await api.post('/sessions/check-out', payload)

      // Create updated session with check-out time
      const updatedSession: Session = {
        ...session,
        evv: {
          ...session.evv,
          checkOutAt: new Date().toISOString(),
          lat: location.lat,
          lng: location.lng,
          checkOutLocation: {
            lat: location.lat,
            lng: location.lng,
            address: location.address,
          },
        },
        status: 'clocked-out' as SessionStatus,
      }

      onSuccess(updatedSession)
      onClose()
    } catch (error: any) {
      setLocationError(
        error.response?.data?.message ||
          'Failed to clock out. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const getSessionType = () => {
    return 'Physical Therapy' // This should come from contract.service or session data
  }

  const getModality = () => {
    return session.locationType || 'in-person'
  }

  const getSlotDuration = () => {
    return session.slotDurationMinutes || session.durationMinutes || 60
  }

  const canSubmit =
    location &&
    !verifying &&
    isAgree &&
    hasSignature &&
    (signedByType === 'patient' ||
      (signedByType === 'family' &&
        relationship !== '' &&
        signedByName.trim() !== ''))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOnIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Session Confirmation & Signature
              </Typography>
              <Typography variant="caption" color="text.secondary">
               Session ID: {session.id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}
      >
        {/* Session Information */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.secondary"
            mb={2}
          >
            Session Information
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Patient Name
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {patient.name}
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Provider Name
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {therapist.name}
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TherapyIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Session Type
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {getSessionType()}
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Date & Time of Session
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {format(new Date(session.scheduledAt), 'MMM dd, yyyy h:mm a')}
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Slot Duration
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {getSlotDuration()} minutes
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOnIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Modality
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="primary">
                {getModality()}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Acknowledgment */}
        <Box mb={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAgree}
                onChange={e => setIsAgree(e.target.checked)}
                color="primary"
              />
            }
            label="I acknowledge that I received therapy services on the above date(s) as described."
          />
        </Box>

        {/* Signature Area */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.secondary"
            mb={2}
          >
            Signature
          </Typography>
          <Paper
            elevation={0}
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              mb: 1,
              bgcolor: 'white',
              display: 'flex',
              justifyContent: 'center',
              overflow: 'auto',
              '& canvas': {
                backgroundColor: 'white',
                borderRadius: '4px',
                cursor: 'crosshair',
                maxWidth: '100%',
                height: 'auto',
              },
            }}
          >
            <SignatureCanvas
              ref={signaturePadRef}
              onEnd={handleSignatureEnd}
              canvasProps={{
                width: 600,
                height: 150,
                className: 'signature-canvas',
                style: { maxWidth: '100%', height: 'auto' },
              }}
            />
          </Paper>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearSignature}
            sx={{ textTransform: 'none' }}
          >
            Clear Signature
          </Button>
        </Box>

        {/* Signee Details */}
        <Box mb={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                minWidth: 'fit-content',
              }}
            >
              Signed By:
            </Typography>
            <RadioGroup
              row
              value={signedByType}
              onChange={e =>
                setSignedByType(e.target.value as 'patient' | 'family')
              }
              sx={{ margin: 0 }}
            >
              <FormControlLabel
                value="patient"
                control={<Radio />}
                label="Patient"
                sx={{ marginRight: 2 }}
              />
              <FormControlLabel
                value="family"
                control={<Radio />}
                label="Guardian"
                sx={{ margin: 0 }}
              />
            </RadioGroup>
          </Box>

          <Slide
            direction="up"
            in={signedByType === 'family'}
            mountOnEnter
            unmountOnExit
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Relationship with Patient</InputLabel>
                  <Select
                    value={relationship}
                    onChange={e => setRelationship(e.target.value)}
                    label="Relationship with Patient"
                  >
                    {relationshipOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={signedByName}
                  onChange={e => setSignedByName(e.target.value)}
                  required
                />
              </Grid>
            </Grid>
          </Slide>

          <TextField
            fullWidth
            label="Date"
            value={format(new Date(), 'MM/dd/yyyy')}
            disabled
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Location Details */}
        {verifying && (
          <Box
            mb={3}
            sx={{
              minHeight: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'primary.main',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              p: 2,
            }}
          >
            <EarthLoader message="Fetching your location..." />
          </Box>
        )}

        {location && !verifying && (
          <Box mb={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" alignItems="start" gap={1.5}>
                <LocationOnIcon fontSize="small" color="primary" />
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600} mb={0.5}>
                    {location.address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Lat: {location.lat.toFixed(6)}, Lng:{' '}
                    {location.lng.toFixed(6)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        {locationError && !verifying && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {locationError}
          </Alert>
        )}

        {/* Map View - Moved to bottom */}
        {location && isLoaded && (
          <Box mb={2}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={15}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
            >
              <Marker
                position={{ lat: location.lat, lng: location.lng }}
                onClick={() => setInfoWindowOpen(true)}
                icon={{
                  url:
                    'data:image/svg+xml;base64,' +
                    btoa(`
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="24" cy="24" r="20" fill="#2196F3" opacity="0.3"/>
                      <circle cx="24" cy="24" r="16" fill="#2196F3" opacity="0.5"/>
                      <circle cx="24" cy="24" r="12" fill="#2196F3"/>
                      <circle cx="24" cy="24" r="6" fill="white"/>
                    </svg>
                  `),
                  scaledSize: new window.google.maps.Size(48, 48),
                  anchor: new window.google.maps.Point(24, 24),
                }}
              >
                {infoWindowOpen && (
                  <InfoWindow
                    onCloseClick={() => setInfoWindowOpen(false)}
                    position={{ lat: location.lat, lng: location.lng }}
                  >
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} mb={0.5}>
                        Provider Location
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {location.address}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Lat: {location.lat.toFixed(6)}, Lng:{' '}
                        {location.lng.toFixed(6)}
                      </Typography>
                    </Box>
                  </InfoWindow>
                )}
              </Marker>
            </GoogleMap>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleClockOut}
          disabled={!canSubmit || loading}
          startIcon={
            loading ? <CircularProgress size={16} /> : <CheckCircleIcon />
          }
          sx={{
            minWidth: 120,
          }}
        >
          Save & Next
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ClockOutDialog
